import React, { useCallback, useEffect, useState } from 'react'
import { MuiTelInput } from 'mui-tel-input'
import { useAuthContext } from '../../hooks/useAuthContext'
import { updateUser, getUserInfo } from '../../helper/userHelper'
import Snackbar from '../../components/Snackbar/Snackbar'
import type { User } from '../../context/AuthContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AUTH_INPUT_CLASS } from '@/components/auth/AuthPageShell'
import { cn } from '@/lib/utils'
import { Radio, UserRound } from 'lucide-react'

type ProfileFieldKey = Extract<
    keyof User,
    'firstName' | 'lastName' | 'email' | 'phoneNumber' | 'organization' | 'allowNotifications'
>

type ProfileFieldCell = {
    label: string
    value: string | boolean | undefined | null
    type: 'text' | 'phone' | 'boolean'
}

type DataRow = [ProfileFieldCell, { editing: boolean }, { key: ProfileFieldKey }]

function buildInitialData(u: User | null): DataRow[] {
    return [
        [{ label: 'First Name', value: u?.firstName ?? '', type: 'text' }, { editing: false }, { key: 'firstName' }],
        [{ label: 'Last Name', value: u?.lastName ?? '', type: 'text' }, { editing: false }, { key: 'lastName' }],
        [{ label: 'Email', value: u?.email ?? '', type: 'text' }, { editing: false }, { key: 'email' }],
        [{ label: 'Phone', value: u?.phoneNumber ?? '', type: 'phone' }, { editing: false }, { key: 'phoneNumber' }],
        [{ label: 'Organization', value: u?.organization ?? '', type: 'text' }, { editing: false }, { key: 'organization' }],
        [
            { label: 'Allow Notifications', value: u?.allowNotifications ?? false, type: 'boolean' },
            { editing: false },
            { key: 'allowNotifications' },
        ],
    ]
}

const checkboxClass =
    'size-4 shrink-0 rounded border-zinc-600 bg-zinc-950 text-emerald-600 focus:ring-2 focus:ring-emerald-500/40 focus:ring-offset-0 focus:ring-offset-zinc-950 disabled:opacity-50'

const telInputSx = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'rgb(9 9 11)',
        color: 'rgb(244 244 245)',
        borderRadius: '6px',
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgb(82 82 91)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgb(113 113 122)' },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgb(16 185 129)',
        borderWidth: '1px',
    },
    '& .MuiInputLabel-root': { color: 'rgb(161 161 170)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'rgb(16 185 129)' },
} as const

const Profile = () => {
    const { user, dispatch } = useAuthContext()
    const [snackbarProperties, setSnackbarProperties] = useState({ open: false, success: false, message: '' })
    const [termsAccepted, setTermsAccepted] = useState(!!user?.termsAccepted)
    const [data, setData] = useState<DataRow[]>(() => buildInitialData(user))

    /** Load fresh profile when the logged-in user identity is known. Deps omit `user` to avoid a refetch loop after dispatch UPDATE. */
    useEffect(() => {
        if (!user?.user_id) return

        let cancelled = false
        const fetchData = async () => {
            try {
                const response = await getUserInfo(user)
                const json = (await response.json()) as User
                if (!response.ok || cancelled) return
                dispatch({ type: 'UPDATE', payload: json })
                setTermsAccepted(!!json.termsAccepted)
                setData(buildInitialData(json))
            } catch (error) {
                if (cancelled) return
                console.error('Failed to fetch user info:', error)
                setSnackbarProperties({ open: true, success: false, message: 'Failed to load profile data.' })
            }
        }
        void fetchData()
        return () => {
            cancelled = true
        }
    }, [user?.user_id, dispatch]) // eslint-disable-line react-hooks/exhaustive-deps -- refetch when identity changes only

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement> | string, index: number, type: ProfileFieldCell['type']) => {
            setData((prev) =>
                prev.map((row, i) => {
                    if (i !== index) return row
                    const cell = row[0]
                    let nextValue: string | boolean | undefined | null = cell.value
                    if (type === 'boolean' && typeof e !== 'string' && 'target' in e) {
                        nextValue = e.target.checked
                    } else if (type === 'phone' && typeof e === 'string') {
                        nextValue = e
                    } else if (typeof e !== 'string' && 'target' in e) {
                        nextValue = e.target.value
                    }
                    return [{ ...cell, value: nextValue }, row[1], row[2]] as DataRow
                })
            )
        },
        []
    )

    const renderInputField = (row: ProfileFieldCell, index: number, type: ProfileFieldCell['type']) => {
        switch (row.type) {
            case 'text':
                return (
                    <Input
                        id={`profile-${row.label}`}
                        value={typeof row.value === 'string' ? row.value : ''}
                        onChange={(e) => handleChange(e, index, type)}
                        className={AUTH_INPUT_CLASS}
                        autoComplete="off"
                    />
                )
            case 'boolean':
                return (
                    <input
                        type="checkbox"
                        className={checkboxClass}
                        id={`profile-${row.label}`}
                        checked={!!row.value}
                        onChange={(e) => handleChange(e, index, type)}
                    />
                )
            case 'phone':
                return (
                    <MuiTelInput
                        id={`profile-${row.label}`}
                        defaultCountry="US"
                        value={typeof row.value === 'string' ? row.value : ''}
                        onChange={(val) => handleChange(val, index, type)}
                        sx={telInputSx}
                        className="w-full"
                    />
                )
            default:
                return null
        }
    }

    const handleEdit = (index: number) => {
        setData((prev) =>
            prev.map((row, i) =>
                i === index ? ([row[0], { editing: !row[1].editing }, row[2]] as DataRow) : row
            )
        )
    }

    const handleSubmit = async () => {
        if (!user) return
        const newUser = { ...user }
        data.forEach((item) => {
            const key = item[2].key
            ;(newUser as Record<string, unknown>)[key] = item[0].value
        })
        newUser.termsAccepted = termsAccepted

        try {
            const response = await updateUser(newUser)
            if (response.ok) {
                const json = (await response.json()) as User
                dispatch({ type: 'UPDATE', payload: json })
                setData((prev) =>
                    prev.map((item) => [item[0], { editing: false }, item[2]] as DataRow)
                )
                setSnackbarProperties({ open: true, success: true, message: 'Profile updated successfully' })
            } else {
                setSnackbarProperties({ open: true, success: false, message: 'Profile update failed' })
            }
        } catch (error) {
            console.error('Failed to update profile:', error)
            setSnackbarProperties({ open: true, success: false, message: 'An error occurred during profile update.' })
        }
    }

    const checkIfDeviceControllerConnected = () => {
        if (!user?.latest_ping_from_device_controller) return false
        const latestPing = new Date(user.latest_ping_from_device_controller)
        if (Number.isNaN(latestPing.getTime())) return false
        const now = new Date()
        const diff = now.getTime() - latestPing.getTime()
        return diff < 300_000
    }

    return (
        <div className="mx-auto w-full max-w-3xl px-4 pb-12 pt-6 sm:px-5">
            <Snackbar properties={snackbarProperties} />

            <div className="space-y-6">
                <Card className="border-zinc-700 bg-zinc-950/75 text-zinc-100 shadow-lg ring-1 ring-zinc-800/60">
                    <CardHeader className="space-y-1 border-b border-zinc-800/80 pb-4">
                        <div className="flex items-center gap-3">
                            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/35">
                                <UserRound className="size-5" aria-hidden />
                            </div>
                            <div>
                                <CardTitle className="text-lg font-semibold tracking-tight text-zinc-50">
                                    User profile
                                </CardTitle>
                                <CardDescription className="text-zinc-400">
                                    Account details and notification preferences.
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-0 pt-2">
                        {data.map((item, index) => {
                            const row = item[0]
                            const isEditing = item[1].editing
                            const isAllowNotificationsRow = row.label === 'Allow Notifications'

                            return (
                                <React.Fragment key={item[2].key}>
                                    <div
                                        className={cn(
                                            'flex flex-col gap-3 border-b border-zinc-800/80 py-4 last:border-b-0 sm:flex-row sm:items-start sm:gap-4'
                                        )}
                                    >
                                        <div className="min-w-[8rem] shrink-0 pt-0.5 text-sm font-medium text-zinc-300 sm:w-44">
                                            {row.label}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            {isAllowNotificationsRow ? (
                                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                                                    <input
                                                        type="checkbox"
                                                        className={cn(checkboxClass, 'mt-1')}
                                                        id={`profile-notify-${row.label}`}
                                                        checked={!!row.value}
                                                        onChange={(e) => handleChange(e, index, row.type)}
                                                        disabled={!isEditing}
                                                    />
                                                    <p className="text-sm leading-relaxed text-zinc-400">
                                                        By providing your phone number and selecting this checkbox, you agree to receive
                                                        informational text messages from IntelliGardener. Consent is not a condition of
                                                        purchase or service. Message frequency will vary. Msg &amp; data rates may apply.
                                                        Reply HELP for help or STOP to cancel.
                                                    </p>
                                                </div>
                                            ) : isEditing ? (
                                                <div className="space-y-1.5">
                                                    {row.type !== 'boolean' ? (
                                                        <Label htmlFor={`profile-${row.label}`} className="sr-only">
                                                            {row.label}
                                                        </Label>
                                                    ) : null}
                                                    {renderInputField(row, index, row.type)}
                                                </div>
                                            ) : ![null, undefined].includes(row.value as null | undefined) ? (
                                                <p className="text-sm text-zinc-200">
                                                    {row.type === 'boolean' ? (row.value ? 'Yes' : 'No') : String(row.value)}
                                                </p>
                                            ) : (
                                                <p className="text-sm text-zinc-500">
                                                    {row.type === 'boolean' ? 'No' : '—'}
                                                </p>
                                            )}
                                        </div>
                                        <div className="shrink-0 sm:pt-0.5">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className="w-full border-zinc-600 bg-zinc-900 text-zinc-100 hover:bg-zinc-800 sm:w-auto"
                                                onClick={() => handleEdit(index)}
                                            >
                                                {isEditing ? 'Done' : 'Edit'}
                                            </Button>
                                        </div>
                                    </div>

                                    {isAllowNotificationsRow ? (
                                        <div className="flex flex-col gap-3 border-b border-zinc-800/80 py-4 sm:flex-row sm:items-center sm:justify-between">
                                            <p className="text-sm text-zinc-400">
                                                I accept the{' '}
                                                <a
                                                    href="https://www.intelligardener.com/terms-of-use"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-emerald-400 underline-offset-2 hover:underline"
                                                >
                                                    Terms of Service
                                                </a>{' '}
                                                &amp;{' '}
                                                <a
                                                    href="https://www.intelligardener.com/privacy-policy"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-emerald-400 underline-offset-2 hover:underline"
                                                >
                                                    Privacy Policy
                                                </a>
                                            </p>
                                            <input
                                                type="checkbox"
                                                className={checkboxClass}
                                                checked={termsAccepted}
                                                onChange={(e) => setTermsAccepted(e.target.checked)}
                                                aria-label="Accept Terms of Service and Privacy Policy"
                                            />
                                        </div>
                                    ) : null}
                                </React.Fragment>
                            )
                        })}
                    </CardContent>
                </Card>

                <Button
                    type="button"
                    onClick={() => void handleSubmit()}
                    className="h-11 w-full bg-emerald-600 px-6 font-semibold text-white shadow hover:bg-emerald-500 sm:w-auto"
                >
                    Save changes
                </Button>

                <Card className="border-zinc-700 bg-zinc-950/75 text-zinc-100 shadow-lg ring-1 ring-zinc-800/60">
                    <CardHeader className="space-y-1 border-b border-zinc-800/80 pb-4">
                        <div className="flex items-center gap-3">
                            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/35">
                                <Radio className="size-5" aria-hidden />
                            </div>
                            <div>
                                <CardTitle className="text-lg font-semibold tracking-tight text-zinc-50">
                                    Device controller
                                </CardTitle>
                                <CardDescription className="text-zinc-400">
                                    Last connectivity signal from your local controller (within 5 minutes counts as connected).
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-2">
                        <div className="flex flex-col justify-between gap-2 rounded-lg border border-zinc-800/80 bg-zinc-900/50 px-4 py-3 sm:flex-row sm:items-center">
                            <span className="text-sm font-medium text-zinc-300">Device controller connected</span>
                            <span
                                className={cn(
                                    'text-sm font-semibold',
                                    checkIfDeviceControllerConnected() ? 'text-emerald-400' : 'text-zinc-500'
                                )}
                            >
                                {checkIfDeviceControllerConnected() ? 'Yes' : 'No'}
                            </span>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}

export default Profile
